import {
  Modal,
  ModalProps,
  Stepper,
  StepperItem,
  StepperWC,
} from '@pypestream/design-system';
import { useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FormProvider } from 'react-hook-form';

import { CreateProjectSteps, ProjectFormFields } from '../..';
import {
  useGlobalAppCtxSelector,
  useManagerCtxSelector,
} from '../../../xstate/app.xstate';
import { useProjectWizardForm } from './useProjectWizardForm';

export type HomepageModalPropsType = {
  modalRef: React.RefObject<ModalProps> | null;
};

export const {
  ProjectNameField,
  ProjectDescriptionField,
  ProjectIconField,
  TimeZoneSelect,
  ProjectEnvironmentField,
  ProjectToolField,
} = new ProjectFormFields();

export const CreateProjectFlow = ({ modalRef }: HomepageModalPropsType) => {
  const swiperRef = useRef<StepperWC>(null);

  const { products, tools } = useManagerCtxSelector((ctx) => ({
    products: ctx.products,
    tools: ctx.tools,
  }));
  const productTools = useMemo(() => {
    return tools?.filter(({ productId }) => {
      return products?.find(({ id }) => id === productId);
    });
  }, [tools, products]);

  const {
    resetFormState,
    createProject,
    projectInfoForm,
    projectEnvironmentsForm,
    projectToolingForm,
    projectLocalizationForm,
    handleNextStep,
    handlePrevStep,
  } = useProjectWizardForm(swiperRef);

  const {
    ProjectInfo,
    ProjectEnvironments,
    ProjectTooling,
    ProjectLocalization,
    ProjectCreated,
  } = CreateProjectSteps;

  const enableEnvironmentsAndWebAppletsUI = useGlobalAppCtxSelector(
    (ctx) => !!ctx.featureFlags?.['environments-and-web-applets']
  );

  const handleModalClose = () => {
    if (modalRef?.current) {
      modalRef.current.open = false;
      resetFormState();
    }
  };

  return (
    <>
      {createPortal(
        <Modal
          test-id="create-project-modal"
          ref={modalRef}
          usePortal={false}
          size="large"
          onClose={handleModalClose}
        >
          <Stepper id="stepper" ref={swiperRef}>
            <StepperItem name="project-info">
              <FormProvider {...projectInfoForm}>
                <ProjectInfo nextStep={handleNextStep} />
              </FormProvider>
            </StepperItem>
            {enableEnvironmentsAndWebAppletsUI && (
              <StepperItem name="project-environments">
                <FormProvider {...projectEnvironmentsForm}>
                  <ProjectEnvironments
                    nextStep={handleNextStep}
                    prevStep={handlePrevStep}
                  />
                </FormProvider>
              </StepperItem>
            )}
            <StepperItem name="project-tools">
              <FormProvider {...projectToolingForm}>
                <ProjectTooling
                  nextStep={handleNextStep}
                  prevStep={handlePrevStep}
                  products={productTools}
                />
              </FormProvider>
            </StepperItem>
            <StepperItem name="project-localization">
              <FormProvider {...projectLocalizationForm}>
                <ProjectLocalization
                  createProject={createProject}
                  nextStep={handleNextStep}
                  prevStep={handlePrevStep}
                />
              </FormProvider>
            </StepperItem>
            <StepperItem name="project-created">
              <ProjectCreated closeModal={handleModalClose} />
            </StepperItem>
          </Stepper>
        </Modal>,
        document.body
      )}
    </>
  );
};
